import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Button,
  Chip,
  TextField,
  Snackbar,
  CircularProgress,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { usersAPI, authAPI, handleAPIError } from '../services/api';

// Roles assignable from the panel. OWNER is intentionally absent — transferring
// ownership is a platform-operator action, never exposed here.
const ASSIGNABLE_ROLES = ['MEMBER', 'ADMIN'];

const UsersPage = () => {
  const { tenantId, scriptHash } = useParams();
  const currentUser = authAPI.getCurrentUser();

  const [rows, setRows] = useState([]);
  const [ownerPrincipalUserId, setOwnerPrincipalUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(
    async (q = '') => {
      setLoading(true);
      setError('');
      try {
        const data = await usersAPI.list(tenantId, scriptHash, q ? { search: q } : {});
        setRows(data.users || []);
        setOwnerPrincipalUserId(data.ownerPrincipalUserId || null);
      } catch (err) {
        setError(handleAPIError(err));
      } finally {
        setLoading(false);
      }
    },
    [tenantId, scriptHash]
  );

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (membershipId, payload) => {
    setBusyId(membershipId);
    try {
      await usersAPI.update(tenantId, scriptHash, membershipId, payload);
      await load(search);
      setSnack({ severity: 'success', message: 'Member updated' });
    } catch (err) {
      setSnack({ severity: 'error', message: handleAPIError(err) });
    } finally {
      setBusyId(null);
    }
  };

  // --- Reset-password dialog state ---
  const [pwTarget, setPwTarget] = useState(null); // the membership row being reset
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  const openResetPassword = (m) => {
    setPwTarget(m);
    setPwForm({ password: '', confirm: '' });
    setShowPw(false);
    setShowConfirmPw(false);
    setPwError('');
  };

  const closeResetPassword = () => {
    setPwTarget(null);
    setPwForm({ password: '', confirm: '' });
    setPwError('');
  };

  const mismatch = pwForm.confirm.length > 0 && pwForm.password !== pwForm.confirm;
  const pwValid =
    pwForm.password.length >= 6 && pwForm.confirm.length > 0 && !mismatch;

  const submitResetPassword = async () => {
    if (!pwTarget || !pwValid) return;
    setPwLoading(true);
    setPwError('');
    try {
      await usersAPI.resetPassword(tenantId, scriptHash, pwTarget.id, {
        password: pwForm.password,
      });
      const who = pwTarget.username;
      closeResetPassword();
      setSnack({ severity: 'success', message: `Password reset for ${who}` });
    } catch (err) {
      setPwError(handleAPIError(err));
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Members
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Manage who can access this chat and their roles.
      </Typography>

      <Box display="flex" gap={1} mt={2} mb={2}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search by username or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(search)}
          style={{ minWidth: 280 }}
        />
        <Button variant="outlined" onClick={() => load(search)}>
          Search
        </Button>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box p={2} color="text.secondary">
                      No members found.
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((m) => {
                const isOwner = m.isOwnerPrincipal;
                const isSelf = currentUser && m.userId === currentUser.id;
                // The owner principal and your own row cannot be modified here.
                const locked = isOwner || isSelf;
                const busy = busyId === m.id;

                return (
                  <TableRow key={m.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {m.username}
                        {isOwner && <Chip size="small" color="primary" label="Owner" />}
                        {isSelf && !isOwner && (
                          <Chip size="small" variant="outlined" label="You" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{m.email || '—'}</TableCell>
                    <TableCell>
                      {isOwner ? (
                        <Chip size="small" label="OWNER" />
                      ) : (
                        <Select
                          value={ASSIGNABLE_ROLES.includes(m.role) ? m.role : 'MEMBER'}
                          disabled={locked || busy}
                          onChange={(e) => patch(m.id, { role: e.target.value })}
                        >
                          {ASSIGNABLE_ROLES.map((r) => (
                            <MenuItem key={r} value={r}>
                              {r}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.75}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={m.isOnline ? 'Online' : 'Offline'}
                        />
                        {m.isBanned && <Chip size="small" color="secondary" label="Banned" />}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                        <Tooltip
                          title={
                            isOwner
                              ? 'The chat owner cannot be modified here'
                              : isSelf
                              ? 'You cannot reset your own password here'
                              : ''
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={locked || busy}
                              onClick={() => openResetPassword(m)}
                            >
                              Reset password
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            isOwner
                              ? 'The chat owner cannot be modified here'
                              : isSelf
                              ? 'You cannot modify your own membership'
                              : ''
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color={m.isBanned ? 'primary' : 'secondary'}
                              disabled={locked || busy}
                              onClick={() => patch(m.id, { isBanned: !m.isBanned })}
                            >
                              {busy ? '…' : m.isBanned ? 'Unban' : 'Ban'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog
        open={!!pwTarget}
        onClose={pwLoading ? undefined : closeResetPassword}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset password for {pwTarget?.username}</DialogTitle>
        <DialogContent>
          {pwError && (
            <Alert severity="error" style={{ marginTop: 8, marginBottom: 8 }}>
              {pwError}
            </Alert>
          )}
          <TextField
            label="New password"
            type={showPw ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            margin="normal"
            value={pwForm.password}
            onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })}
            helperText="Password must be at least 6 characters long"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPw(!showPw)} edge="end" size="small">
                      {showPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="Confirm new password"
            type={showConfirmPw ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            margin="normal"
            value={pwForm.confirm}
            onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
            error={mismatch}
            helperText={mismatch ? 'Passwords do not match' : ''}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPw(!showConfirmPw)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResetPassword} disabled={pwLoading}>
            Cancel
          </Button>
          <Button
            onClick={submitResetPassword}
            variant="contained"
            color="primary"
            disabled={!pwValid || pwLoading}
          >
            {pwLoading ? <CircularProgress size={20} /> : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
